// @flow

import { getArrayBuffer } from "../util/ajax";
import window from "../util/window";
import { RasterWorkerTileResult } from "../util/web_worker_transfer";

// import WorkerRasterTile from "./worker_raster_tile";
import type { WorkerRasterTileParameters } from "../source/worker_source";

import type { PerformanceResourceTiming } from "../types/performance_resource_timing";
import type Actor from "../util/actor";
import type StyleLayerIndex from "../style/style_layer_index";
import type { Callback } from "../types/callback";

function loadImageBuffer(requestParameters, callback) {
    return getArrayBuffer(requestParameters, (err, imgData) => {
        if (err) {
            callback(err);
        } else if (imgData) {
            const blob = new window.Blob([new Uint8Array(imgData.data)], { type: "image/png" });
            const data = new RasterWorkerTileResult(blob, window.URL.createObjectURL(blob));
            callback(null, data);
        }
    });
}

export type LoadVectorTileResult = {
    vectorTile: VectorTile,
    rawData: ArrayBuffer,
    expires?: any,
    cacheControl?: any,
    resourceTiming?: Array<PerformanceResourceTiming>,
};

/**
 * @callback LoadVectorDataCallback
 * @param error
 * @param vectorTile
 * @private
 */
export type LoadVectorDataCallback = Callback<?LoadVectorTileResult>;

export type AbortVectorData = () => void;
export type LoadVectorData = (params: WorkerTileParameters, callback: LoadVectorDataCallback) => ?AbortVectorData;

/**
 * The {@link WorkerSource} implementation that supports {@link VectorTileSource}.
 * This class is designed to be easily reused to support custom source types
 * for data formats that can be parsed/converted into an in-memory VectorTile
 * representation.  To do so, create it with
 * `new VectorTileWorkerSource(actor, styleLayers, customLoadVectorDataFunction)`.
 *
 * @private
 */
class RasterTileWorkerSource implements WorkerSource {
    actor: Actor;
    layerIndex: StyleLayerIndex;
    loadVectorData: LoadVectorData;
    loading: { [string]: WorkerTile };
    loaded: { [string]: WorkerTile };

    /**
     * @param [loadVectorData] Optional method for custom loading of a VectorTile
     * object based on parameters passed from the main-thread Source. See
     * {@link VectorTileWorkerSource#loadTile}. The default implementation simply
     * loads the pbf at `params.url`.
     */
    constructor(actor: Actor, layerIndex: StyleLayerIndex) {
        this.actor = actor;
        this.layerIndex = layerIndex;
        this.loading = {};
        this.loaded = {};
    }

    /**
     * Implements {@link WorkerSource#loadTile}. Delegates to
     * {@link VectorTileWorkerSource#loadVectorData} (which by default expects
     * a `params.url` property) for fetching and producing a VectorTile object.
     */
    loadTile(params: WorkerRasterTileParameters, callback: WorkerTileCallback) {
        const uid = params.uid;

        if (!this.loading) this.loading = {};
        const workerTile = (this.loading[uid] = {});
        workerTile.request = loadImageBuffer(params.request, callback);
    }

    /**
     * Implements {@link WorkerSource#abortTile}.
     *
     * @param params
     * @param params.uid The UID for this tile.
     */
    abortTile(params: TileParameters, callback: WorkerTileCallback) {
        const loading = this.loading,
            uid = params.uid;
        if (loading && loading[uid] && loading[uid].request) {
            loading[uid].request.cancel();
            delete loading[uid];
        }
        callback();
    }

    /**
     * Implements {@link WorkerSource#removeTile}.
     *
     * @param params
     * @param params.uid The UID for this tile.
     */
    removeTile(params: TileParameters, callback: WorkerTileCallback) {
        const loaded = this.loaded,
            uid = params.uid;
        if (loaded && loaded[uid]) {
            delete loaded[uid];
        }
        callback();
    }
}

export default RasterTileWorkerSource;
